import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Gauge,
  DollarSign,
  Clock,
  Zap,
  Shield,
  Activity,
} from 'lucide-react'
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { api } from '../../lib/api'
import type {
  ThrottleSnapshot,
  BudgetSnapshot,
  CostForecast,
  TokenUsageSummary,
  AiOpsEvent,
  RoutingState,
} from '../../lib/api'

// ─── Colors ───

const BAND_COLORS: Record<string, { bg: string; text: string; label: string; intent: string }> = {
  T0: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', label: 'All Local', intent: 'Strict cost control; cloud blocked' },
  T1: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Local Preferred', intent: 'Local first; cheap cloud fallback' },
  T2: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Balanced Hybrid', intent: 'Local for utility; cloud for writing/review' },
  T3: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'Quality Preferred', intent: 'Premium cloud for generate/council' },
  T4: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', label: 'All Premium', intent: 'Premium-first; local by exception' },
}

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316']

const TIER_BADGE: Record<string, string> = {
  local: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  economy: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  balanced: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  high: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  highest: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  bypass: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
}

// ─── Main Component ───

export function AiOpsTab() {
  const queryClient = useQueryClient()
  const [usagePeriod, setUsagePeriod] = useState(7)

  const { data: throttle } = useQuery<ThrottleSnapshot>({
    queryKey: ['health-throttle'],
    queryFn: () => api.health.throttle(),
    refetchInterval: 30_000,
  })

  const { data: budget } = useQuery<BudgetSnapshot>({
    queryKey: ['health-budgets'],
    queryFn: () => api.health.budgets(),
    refetchInterval: 60_000,
  })

  const { data: forecast } = useQuery<CostForecast>({
    queryKey: ['health-forecast'],
    queryFn: () => api.health.forecast(),
    refetchInterval: 60_000,
  })

  const { data: usage } = useQuery<TokenUsageSummary>({
    queryKey: ['health-usage', usagePeriod],
    queryFn: () => api.health.tokenUsage(usagePeriod),
    refetchInterval: 60_000,
  })

  const { data: routing } = useQuery<RoutingState>({
    queryKey: ['health-routing'],
    queryFn: () => api.health.routing(),
    refetchInterval: 60_000,
  })

  const { data: eventsData } = useQuery<{ events: AiOpsEvent[] }>({
    queryKey: ['health-events'],
    queryFn: () => api.health.events(30),
    refetchInterval: 30_000,
  })

  const throttleMutation = useMutation({
    mutationFn: (value: number) => api.health.setThrottle(value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['health-throttle'] })
      queryClient.invalidateQueries({ queryKey: ['health-routing'] })
    },
  })

  const budgetMutation = useMutation({
    mutationFn: (data: { capUsd?: number; enforcementMode?: string; alertThresholds?: number[] }) =>
      api.health.setBudgets(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['health-budgets'] })
    },
  })

  return (
    <div className="space-y-6">
      {/* Row 1: Throttle + Budget side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ThrottleLever
          throttle={throttle ?? null}
          onSet={(v) => throttleMutation.mutate(v)}
          isPending={throttleMutation.isPending}
        />
        <BudgetPanel
          budget={budget ?? null}
          forecast={forecast ?? null}
          onUpdate={(d) => budgetMutation.mutate(d)}
        />
      </div>

      {/* Row 2: Usage mix charts */}
      <UsageMix
        usage={usage ?? null}
        period={usagePeriod}
        onPeriodChange={setUsagePeriod}
      />

      {/* Row 3: Routing matrix + Event feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RoutingMatrix throttle={throttle ?? null} routing={routing ?? null} />
        <EventFeed events={eventsData?.events ?? []} />
      </div>
    </div>
  )
}

// ─── Throttle Lever ───

function ThrottleLever({
  throttle,
  onSet,
  isPending,
}: {
  throttle: ThrottleSnapshot | null
  onSet: (value: number) => void
  isPending: boolean
}) {
  const [localValue, setLocalValue] = useState<number | null>(null)
  const displayValue = localValue ?? throttle?.value ?? 45
  const band = throttle?.band ?? 'T2'
  const bandInfo = BAND_COLORS[band] ?? BAND_COLORS['T2']!

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(Number(e.target.value))
  }, [])

  const handleCommit = useCallback(() => {
    if (localValue !== null) {
      onSet(localValue)
      setLocalValue(null)
    }
  }, [localValue, onSet])

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Gauge className="w-4 h-4" />
          Throttle Control
        </h3>
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${bandInfo.bg} ${bandInfo.text}`}>
          {band} - {bandInfo.label}
        </span>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 dark:text-gray-400 w-6">0</span>
          <input
            type="range"
            min={0}
            max={100}
            value={displayValue}
            onChange={handleChange}
            onMouseUp={handleCommit}
            onTouchEnd={handleCommit}
            disabled={isPending}
            className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
          />
          <span className="text-xs text-gray-500 dark:text-gray-400 w-8">{displayValue}</span>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">{bandInfo.intent}</p>

        {/* Band markers */}
        <div className="flex text-[10px] text-gray-400 dark:text-gray-500">
          <span className="flex-1">T0</span>
          <span className="flex-1 text-center">T1</span>
          <span className="flex-1 text-center">T2</span>
          <span className="flex-1 text-center">T3</span>
          <span className="flex-1 text-right">T4</span>
        </div>
      </div>
    </div>
  )
}

// ─── Budget Panel ───

function BudgetPanel({
  budget,
  forecast,
  onUpdate,
}: {
  budget: BudgetSnapshot | null
  forecast: CostForecast | null
  onUpdate: (data: { capUsd?: number; enforcementMode?: string }) => void
}) {
  const capUsd = budget?.capUsd ?? 0
  const usedUsd = budget?.usedUsd ?? 0
  const remaining = budget?.remainingUsd ?? 0
  const usedPct = capUsd > 0 ? Math.min(100, (usedUsd / capUsd) * 100) : 0
  const enforcementMode = budget?.enforcementMode ?? 'monitor'

  const progressColor = usedPct >= 90 ? 'bg-red-500' : usedPct >= 75 ? 'bg-amber-500' : usedPct >= 50 ? 'bg-yellow-500' : 'bg-green-500'

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <DollarSign className="w-4 h-4" />
          Monthly Budget
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">{budget?.month ?? ''}</span>
      </div>

      <div className="space-y-3">
        {/* Progress bar */}
        <div>
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span>${usedUsd.toFixed(2)} used</span>
            <span>{capUsd > 0 ? `$${capUsd.toFixed(2)} cap` : 'No cap set'}</span>
          </div>
          {capUsd > 0 && (
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
              <div
                className={`h-2 rounded-full transition-all ${progressColor}`}
                style={{ width: `${usedPct}%` }}
              />
            </div>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {capUsd > 0 ? `$${remaining.toFixed(2)}` : '--'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Forecast</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {forecast ? `$${forecast.forecastExpected.toFixed(2)}` : '--'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Days Left</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {forecast?.daysRemaining ?? '--'}
            </p>
          </div>
        </div>

        {/* Enforcement mode */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500 dark:text-gray-400">Mode:</span>
          {(['monitor', 'soft-cap', 'hard-cap'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => onUpdate({ enforcementMode: mode })}
              className={`px-2 py-0.5 rounded text-xs transition-colors ${
                enforcementMode === mode
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Usage Mix ───

function UsageMix({
  usage,
  period,
  onPeriodChange,
}: {
  usage: TokenUsageSummary | null
  period: number
  onPeriodChange: (d: number) => void
}) {
  const providerData = usage
    ? Object.entries(usage.byProvider).map(([name, stats]) => ({ name, value: stats.calls }))
    : []
  const purposeData = usage
    ? Object.entries(usage.byPurpose).map(([name, stats]) => ({ name, value: stats.calls }))
    : []

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Usage Mix
        </h3>
        <div className="flex gap-1 text-xs">
          {[7, 30].map((d) => (
            <button
              key={d}
              onClick={() => onPeriodChange(d)}
              className={`px-2 py-0.5 rounded ${
                period === d
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <Stat label="Total Calls" value={usage?.totalCalls?.toLocaleString() ?? '0'} />
        <Stat label="Input Tokens" value={formatTokens(usage?.totalInputTokens ?? 0)} />
        <Stat label="Output Tokens" value={formatTokens(usage?.totalOutputTokens ?? 0)} />
        <Stat label="Est. Cost" value={`$${(usage?.estimatedCostUsd ?? 0).toFixed(2)}`} />
      </div>

      {/* Pie charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MiniPie title="By Provider" data={providerData} />
        <MiniPie title="By Purpose" data={purposeData} />
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  )
}

function MiniPie({ title, data }: { title: string; data: { name: string; value: number }[] }) {
  if (data.length === 0) {
    return (
      <div className="text-center text-xs text-gray-400 dark:text-gray-500 py-8">
        No data for {title.toLowerCase()}
      </div>
    )
  }

  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 text-center">{title}</p>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={65}
            innerRadius={30}
            paddingAngle={2}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--color-gray-900, #111827)',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              color: '#f3f4f6',
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: '0.625rem' }}
            iconSize={8}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─── Routing Matrix ───

function RoutingMatrix({
  throttle,
  routing,
}: {
  throttle: ThrottleSnapshot | null
  routing: RoutingState | null
}) {
  const purposes = throttle?.purposePolicies
    ? Object.entries(throttle.purposePolicies)
    : []

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4" />
        Routing Matrix
      </h3>

      <div className="overflow-auto max-h-64">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-1.5 px-2 text-gray-500 dark:text-gray-400 font-medium">Purpose</th>
              <th className="text-left py-1.5 px-2 text-gray-500 dark:text-gray-400 font-medium">Effective Tier</th>
              <th className="text-left py-1.5 px-2 text-gray-500 dark:text-gray-400 font-medium">Default Tier</th>
            </tr>
          </thead>
          <tbody>
            {purposes.map(([purpose, tier]) => (
              <tr key={purpose} className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-1.5 px-2 text-gray-900 dark:text-gray-100 font-mono">{purpose}</td>
                <td className="py-1.5 px-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${TIER_BADGE[tier] ?? TIER_BADGE.balanced}`}>
                    {tier}
                  </span>
                </td>
                <td className="py-1.5 px-2">
                  <span className="text-gray-400 dark:text-gray-500">
                    {routing?.config?.purposeTiers?.[purpose] ?? routing?.config?.defaultTier ?? 'balanced'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {routing && (
        <div className="mt-2 flex gap-3 text-[10px] text-gray-400 dark:text-gray-500">
          <span>Default: {routing.config.defaultTier}</span>
          <span>Local: {routing.config.preferLocal ? 'on' : 'off'}</span>
          <span>Council: {routing.config.councilTier}</span>
        </div>
      )}
    </div>
  )
}

// ─── Event Feed ───

function EventFeed({ events }: { events: AiOpsEvent[] }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4" />
        Recent Events
      </h3>

      <div className="space-y-1.5 max-h-64 overflow-auto">
        {events.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">No events yet</p>
        )}
        {events.map((e) => (
          <div
            key={e.id}
            className="flex items-start gap-2 text-xs py-1.5 border-b border-gray-50 dark:border-gray-800 last:border-0"
          >
            {e.type === 'throttle' ? (
              <Gauge className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
            ) : (
              <Shield className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              {e.type === 'throttle' ? (
                <span className="text-gray-700 dark:text-gray-300">
                  Throttle {e.old_band} &rarr; {e.new_band} ({e.old_value} &rarr; {e.new_value})
                </span>
              ) : (
                <span className="text-gray-700 dark:text-gray-300">
                  Budget {e.action_taken} at {e.threshold_pct}%
                </span>
              )}
            </div>
            <span className="text-gray-400 dark:text-gray-500 shrink-0">
              {formatRelativeTime(e.created_at)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Helpers ───

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  return `${days}d`
}
